import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { SLATE, toInputValue, fromInputValue } from './shifts-shared.js';

export const CorrectShiftModal = ({ open, shift, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && shift) {
      setForm({
        clockInAt: toInputValue(shift.clockInAt),
        clockOutAt: toInputValue(shift.clockOutAt),
        breakMinutes: String(shift.breakMinutes ?? 0),
        notes: shift.notes ?? '',
      });
      setError(null);
    }
  }, [open, shift]);

  if (!open || !shift) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const saved = await api.patch(`/shifts/${shift.id}`, {
        clockInAt: fromInputValue(form.clockInAt),
        clockOutAt: form.clockOutAt ? fromInputValue(form.clockOutAt) : null,
        breakMinutes: Number(form.breakMinutes),
        notes: form.notes.trim() || null,
      });
      onSaved?.(saved);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this shift for ${shift.userName ?? 'this user'}? This can't be undone.`)) return;
    try { await api.delete(`/shifts/${shift.id}`); onSaved?.(); }
    catch (err) { alert(err.message ?? 'Failed'); }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: SLATE }}>Correct shift</p>
          <p class="m-0 text-sm text-text-secondary">{shift.userName ?? 'Unknown user'}</p>
        </div>
        <div class="space-y-3 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Clock in</p>
            <input
              type="datetime-local"
              value={form.clockInAt ?? ''}
              onInput={set('clockInAt')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              Clock out <span class="font-medium text-text-muted">· blank = still open</span>
            </p>
            <input
              type="datetime-local"
              value={form.clockOutAt ?? ''}
              onInput={set('clockOutAt')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Break (minutes)</p>
            <input
              type="number"
              min="0"
              value={form.breakMinutes ?? '0'}
              onInput={set('breakMinutes')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Notes</p>
            <textarea
              rows={2}
              value={form.notes ?? ''}
              onInput={set('notes')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
            </p>
          )}
        </div>
        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={busy || !form.clockInAt}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
